/**
 * NotesModal — the post-call window. Opens when a connected call ends and the
 * session is waiting on a disposition.
 *
 * It does NOT let the operator skip. The server holds the next call until an
 * outcome is saved (AGENTS.md: a frontend-only skip would not stop an outbound
 * call, and a skipped "Do not call" is a compliance miss), so there is no Skip
 * button and the timer cannot advance without a choice. The 15-second
 * countdown saves what has been chosen and typed so far; if nothing is chosen
 * when it runs out, the window waits.
 */
import { useEffect, useRef, useState } from 'react';

const COUNTDOWN_SECONDS = 15;

export default function NotesModal({ prospectInfo, dispositions, initialNotes = '', saving = false, error = '', onSubmit }) {
  const [notes, setNotes] = useState(initialNotes);
  const [selected, setSelected] = useState(null);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);

  const needsNotes = Boolean(selected?.requires_notes) && !notes.trim();
  const canSubmit = Boolean(selected) && !needsNotes && !saving;

  // The countdown fires from a timer, so it reads the latest choice through a
  // ref rather than the closure of the render that started it.
  const latest = useRef({});
  latest.current = { selected, notes, canSubmit, onSubmit };

  useEffect(() => {
    const timer = setInterval(() => setCountdown((n) => Math.max(0, n - 1)), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (countdown !== 0) return;
    const { selected: choice, notes: text, canSubmit: ready, onSubmit: submit } = latest.current;
    if (ready) submit({ disposition: choice, notes: text });
  }, [countdown]);

  const waiting = countdown === 0 && !canSubmit;

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Call notes">
      <div className="modal">
        <div className="modal__head">
          <div>
            <h2 className="modal__title">Log this call</h2>
            {(prospectInfo?.company || prospectInfo?.name) && (
              <p className="modal__subtitle">{[prospectInfo.company, prospectInfo.name].filter(Boolean).join(' — ')}</p>
            )}
            <p className="mono modal__phone">{prospectInfo?.from ?? prospectInfo?.phone ?? '—'}</p>
          </div>
          <div className="countdown" role="timer" aria-label={waiting ? 'Waiting for an outcome' : `Saving in ${countdown} seconds`}>
            <svg className="countdown__ring" viewBox="0 0 36 36" aria-hidden="true">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--line)" strokeWidth="2" />
              <circle
                cx="18" cy="18" r="15.9" fill="none" stroke="var(--purple)" strokeWidth="2" strokeLinecap="round"
                strokeDasharray={`${(countdown / COUNTDOWN_SECONDS) * 100} 100`} transform="rotate(-90 18 18)"
              />
            </svg>
            <span className="countdown__num">{countdown}</span>
          </div>
        </div>

        <div className="modal__body">
          <div className="disposition-grid" role="group" aria-label="Outcome">
            {dispositions.map((d) => (
              <button
                key={d.id ?? d.label}
                type="button"
                aria-pressed={selected?.label === d.label}
                className={`disposition-btn ${selected?.label === d.label ? 'disposition-btn--selected' : ''}`}
                onClick={() => setSelected(d)}
              >
                {d.label}
              </button>
            ))}
          </div>

          <textarea
            className="textarea"
            rows={3}
            placeholder="Notes, next steps, what was said…"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {needsNotes && <p className="modal__hint" role="status">“{selected.label}” needs a note before it can be saved.</p>}
          {error && <p className="modal__hint modal__hint--error" role="alert">{error}</p>}
          {waiting && !needsNotes && <p className="modal__hint" role="status">Choose an outcome — the next call stays on hold until one is saved.</p>}
        </div>

        <div className="modal__foot row">
          <button type="button" className="btn btn--primary" disabled={!canSubmit} onClick={() => onSubmit({ disposition: selected, notes })}>
            {saving ? 'Saving…' : 'Save & next call'}
          </button>
        </div>
      </div>
    </div>
  );
}
